// frontend/src/pages/ManageOrders.js
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import axios from 'axios';
import { toast } from 'sonner';
import Select from 'react-select';
import RatingForm from '../components/RatingForm';
import { messaging, onMessage } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { Meteors } from '../components/ui/meteors';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "../components/ui/pagination";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  CardFooter,
} from "../components/ui/card"
import { Input } from "../components/ui/input"
import { Button } from "../components/ui/button"
import { Badge } from "../components/ui/badge";
import Navbar from '../components/Navbar';

const debounce = (func, wait) => {
  let timeout;
  return (...args) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};

const API_URL = process.env.REACT_APP_API_URL;
const WS_PROTOCOL = API_URL.startsWith('https') ? 'wss://' : 'ws://';
const WS_HOST = API_URL.replace(/^https?:\/\//, '');
const WS_URL = `${WS_PROTOCOL}${WS_HOST}`;

// EXACT SAME SPINNER AS ProviderProfile.js — BEAUTIFUL WHITE
const Spinner = () => (
  <div className="absolute inset-0 flex items-center justify-center">
    <div className="w-6 h-6 border-4 border-white border-t-transparent rounded-full animate-spin"></div>
  </div>
);

// Global spinner animation (once)
if (!document.getElementById('manage-orders-spinner')) {
  const style = document.createElement('style');
  style.id = 'manage-orders-spinner';
  style.innerHTML = `
    @keyframes spin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }
  `;
  document.head.appendChild(style);
}

const fetchOrders = async () => {
  const response = await axios.get(`${API_URL}/api/provider_orders/`, { withCredentials: true });
  return response.data;
};

const fetchClientOrders = async () => {
  const response = await axios.get(`${API_URL}/api/client_orders/`, { withCredentials: true });
  return response.data;
};

const fetchOrderProgress = async (requestId) => {
  const response = await axios.get(`${API_URL}/api/order_progress/${requestId}/`, { withCredentials: true });
  return response.data;
};

const statusOptions = [
  { value: 'pending', label: 'Pending' },
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'active', label: 'Active' },
  { value: 'ready_for_payment', label: 'Ready for Payment' },
  { value: 'completed', label: 'Completed' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'voided', label: 'Voided' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'pending_payment', label: 'Pending Payment' },
];

const OrdersPagination = ({ currentPage, totalItems, onPageChange, ordersPerPage = 10 }) => {
  const totalPages = Math.ceil(totalItems / ordersPerPage);
  if (totalPages <= 1) return null;

  return (
    <Pagination className="justify-center mt-10">
      <PaginationContent>
        <PaginationItem>
          <PaginationPrevious 
            onClick={() => onPageChange(Math.max(1, currentPage - 1))}
            className={currentPage === 1 ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
          />
        </PaginationItem>

        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
          const pageNum = Math.max(1, Math.min(totalPages - 4, currentPage - 2)) + i;
          return (
            <PaginationItem key={pageNum}>
              <PaginationLink
                onClick={() => onPageChange(pageNum)}
                isActive={pageNum === currentPage}
                className="cursor-pointer"
              >
                {pageNum}
              </PaginationLink>
            </PaginationItem>
          );
        })}

        <PaginationItem>
          <PaginationNext 
            onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
            className={currentPage === totalPages ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
          />
        </PaginationItem>
      </PaginationContent>
    </Pagination>
  );
};

const OrderSection = React.memo(({
  title,
  orders,
  filters,
  setFilters,
  currentPage,
  setCurrentPage,
  isCompleted,
  isProviderSection,
  sectionKey,
  providerOrders,
  handleAccept,
  handleReject,
  getStatusBadgeVariant,
  getStatusColor,
  showJobDonePanel,
  setShowJobDonePanel,
  showPaymentRequestPanel,
  setShowPaymentRequestPanel,
  jobDoneAmount,
  setJobDoneAmount,
  payoutPhone,
  setPayoutPhone,
  clientPayPhone,
  setClientPayPhone,
  queryClient,
  user,
  orderRefs,
  getMinCharge,
  acceptMutation,
  rejectMutation,
  markJobCompleteMutation,
  initiateStkPushMutation,
  paymentStates,
  setPaymentStates,
}) => {
  const sectionRef = useRef(null);
  const ordersPerPage = 10;

  const filterOrders = useCallback((orders, filters) => {
    if (!orders || !Array.isArray(orders)) return [];
    return orders.filter((req) => {
      const status = req.order?.status || req.status;
      const matchesStatus = !filters.statuses.length || filters.statuses.some(s => s.value === status);
      const matchesSearch = !filters.search || (req.description?.toLowerCase().includes(filters.search.toLowerCase()));
      return matchesStatus && matchesSearch;
    });
  }, []);

  const paginateOrders = useCallback((orders, page) => {
    if (!orders || !Array.isArray(orders)) return [];
    const start = (page - 1) * ordersPerPage;
    return orders.slice(start, start + ordersPerPage);
  }, []);

  const filteredOrders = useMemo(() => filterOrders(orders, filters), [orders, filters, filterOrders]);
  const paginatedOrders = useMemo(() => paginateOrders(filteredOrders, currentPage), [filteredOrders, currentPage, paginateOrders]);

  const aiohttp = useMemo(() => debounce((value) => {
    setFilters(prev => ({ ...prev, search: value }));
    setCurrentPage(1);
  }, 300), [setFilters, setCurrentPage]);

  const handleSearchChange = useCallback((e) => {
    const value = e.target.value;
    setFilters(prev => ({
      ...prev,
      searchInput: { ...prev.searchInput, [sectionKey]: value }
    }));
    aiohttp(value);
  }, [sectionKey, aiohttp, setFilters]);

  const handleStatusChange = useCallback((selected) => {
    setFilters(prev => ({ ...prev, statuses: selected || [] }));
    setCurrentPage(1);
  }, [setFilters, setCurrentPage]);

  const confirmProgressMutation = useMutation({
    mutationFn: async ({ requestId }) => {
      const res = await axios.post(`${API_URL}/api/confirm_progress/${requestId}/`, {}, { withCredentials: true });
      return res.data;
    },
    onSuccess: () => {
      toast.success('Progress confirmed');
      queryClient.invalidateQueries(['orderProgress']);
      queryClient.invalidateQueries(['providerOrders']);
      queryClient.refetchQueries(['providerOrders']);
    },
    onError: () => toast.error('Failed to confirm progress'),
  });

  const handleConfirmProgress = useCallback((requestId) => {
    confirmProgressMutation.mutate({ requestId });
  }, [confirmProgressMutation]);

  return (
    <div className="mb-12" ref={sectionRef}>
      <h2 className="text-2xl font-bold tracking-tight mb-6 text-foreground">{title}</h2>
      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-8">
        <Select 
          isMulti 
          options={statusOptions} 
          value={filters.statuses} 
          onChange={handleStatusChange}
          placeholder="Filter by status..." 
          className="flex-1" 
          classNamePrefix="react-select"
          styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }} 
          menuPortalTarget={document.body} 
        />
        <Input 
          type="text" 
          value={filters.searchInput?.[sectionKey] || ''} 
          onChange={handleSearchChange}
          placeholder="Search description..." 
          className="flex-1 text-2x1"
        />
        <Button 
          variant="destructive" 
          onClick={() => {
            setFilters({ statuses: [], search: '', searchInput: {} });
            setCurrentPage(1);
          }}
        >
          Reset
        </Button>
      </div>

      {paginatedOrders.length === 0 ? (
        <p className="text-muted-foreground text-center py-12">No orders found</p>
      ) : (
        <>
          <div className="space-y-6">
            {paginatedOrders.map((req) => {
              const b2cTx = req.order?.mpesa_transactions?.find(tx => tx.transaction_type === 'B2C');
              const orderId = req.order?.id;
              const paymentState = orderId ? paymentStates[orderId] : null;

              return (
                <Card key={req.id} ref={el => orderRefs.current[req.id] = el} className="overflow-hidden bg-card border border-gray-700/50 dark:border-gray-800 hover:border-gray-600 dark:hover:border-gray-700 transition-colors">
                  <CardHeader>
                    <div className="flex justify-between items-start">
                      <CardTitle className="text-lg font-semibold text-foreground">
                        {isProviderSection ? req.user_username : req.provider_business_name}
                      </CardTitle>
                      <Badge 
                        className={`font-medium px-1.5 py-0.5 text-xs uppercase tracking-[0.5px] ${getStatusBadgeVariant(req.order?.status || req.status)}`}
                      >
                        {(req.order?.status || req.status).replace('_', ' ').toUpperCase()}
                      </Badge>
                    </div>
                    <CardDescription className="text-sm text-muted-foreground">
                      {req.scheduled_at ? new Date(req.scheduled_at).toLocaleString() : '—'}
                    </CardDescription>
                  </CardHeader>

                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-muted-foreground text-xs uppercase tracking-widest block mb-1">Description</span>
                        <p className="font-medium text-foreground">{req.description || '—'}</p>
                      </div>
                      {req.estimated_amount && (
                        <div>
                          <span className="text-muted-foreground text-xs uppercase tracking-widest block mb-1">Client Expected</span>
                          <p className="font-medium text-emerald-400">KSh {req.estimated_amount}</p>
                        </div>
                      )}
                      {req.order && (
                        <div>
                          <span className="text-muted-foreground text-xs uppercase tracking-widest block mb-1">Final Amount</span>
                          <p className="font-medium text-foreground">KSh {req.order.amount}</p>
                        </div>
                      )}
                      {isProviderSection && b2cTx && (
                        <>
                          <div>
                            <span className="text-muted-foreground text-xs uppercase tracking-widest block mb-1">Net Payout</span>
                            <p className="font-medium text-emerald-400">KSh {b2cTx.amount || '—'}</p>
                          </div>
                          <div>
                            <span className="text-muted-foreground text-xs uppercase tracking-widest block mb-1">B2C Fee</span>
                            <p className="font-medium text-foreground">KSh {b2cTx.b2c_fee || '0'}</p>
                          </div>
                        </>
                      )}
                      {req.order?.rating && (
                        <div>
                          <span className="text-muted-foreground text-xs uppercase tracking-widest block mb-1">Rating</span>
                          <p className="font-medium text-foreground">{req.order.rating.rating}/5 by {req.order.rating.display_name}</p>
                        </div>
                      )}
                    </div>

                    {req.status === 'active' && <ProgressTimer requestId={req.id} />}

                    {isProviderSection && req.status === 'active' && (
                      <Button 
                        onClick={() => handleConfirmProgress(req.id)} 
                        className="w-full" 
                        disabled={confirmProgressMutation.isPending}
                      >
                        {confirmProgressMutation.isPending ? 'Confirming...' : 'Confirm Progress'}
                      </Button>
                    )}

                    {(req.status === 'scheduled' || req.status === 'active' || req.status === 'ready_for_payment') && (
                      <Button variant="outline" asChild className="w-full">
                        <Link to={`/profile/?chat_with=${isProviderSection ? req.user_id : req.provider_user_id}`}>
                          💬 Chat with {isProviderSection ? 'Client' : 'Provider'}
                        </Link>
                      </Button>
                    )}

                    {isProviderSection && req.status === 'pending' && (
                      <div className="flex gap-3">
                        <Button 
                          onClick={() => handleAccept(req.id)} 
                          className="flex-1" 
                          size="sm"
                          disabled={acceptMutation.isPending}
                        >
                          {acceptMutation.isPending ? 'Accepting...' : 'Accept Order'}
                        </Button>
                        <Button 
                          onClick={() => handleReject(req.id)} 
                          variant="destructive" 
                          size="sm"
                          className="flex-1"
                          disabled={rejectMutation.isPending}
                        >
                          {rejectMutation.isPending ? 'Rejecting...' : 'Reject'}
                        </Button>
                      </div>
                    )}

                {/* Provider: Job Done Button */}
                {isProviderSection && req.status === 'active' && !req.order && (
                  <div className="mt-8">
                    <Button
		      onClick={() => {
		        setShowJobDonePanel(req.id);
		        setJobDoneAmount(req.estimated_amount || '');
		        setPayoutPhone('');
		      }}
		      className="w-full h-14 text-base font-semibold"
		      size="lg"
 		    >
		      Job Done – Request Payment
		    </Button>
                  </div>
                )}

                {/* Client: Pay Now / Processing / Success / Retry */}
                {!isProviderSection && req.order && ['pending_payment', 'processing'].includes(req.order.status) && (
                  <div className="mt-8">
                    {paymentState === 'processing' ? (
                      <div className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-7 rounded-2xl font-bold text-3xl shadow-2xl flex items-center justify-center gap-3">
                        <Spinner />
                        Processing Payment...
                      </div>
                    ) : paymentState === 'success' ? (
                      <div className="w-full bg-gradient-to-r from-green-600 to-emerald-600 text-white py-7 rounded-2xl font-bold text-3xl shadow-2xl text-center">
                        Payment Complete!
                      </div>
                    ) : (
                      <button
                        onClick={() => {
                          setShowPaymentRequestPanel(req.id);
                          setClientPayPhone('');
                        }}
                        className={`w-full text-white py-7 rounded-2xl font-bold text-3xl shadow-2xl transition-all transform hover:scale-105 ${
                          req.order.status === 'processing' || paymentState === 'failed'
                            ? 'bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-700 hover:to-orange-700'
                            : 'bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700'
                        }`}
                      >
                        {req.order.status === 'processing' || paymentState === 'failed'
                          ? 'Payment Failed – Try Again'
                          : `Pay KSh ${req.order.amount} Now`}
                      </button>
                    )}
                    {paymentState === 'processing' && (
                      <p className="text-center text-blue-300 text-sm mt-4 font-medium animate-pulse">
                        Waiting for M-Pesa confirmation... (up to 10 seconds)
                      </p>
                    )}
                  </div>
                )}

                {/* Provider Job Done Modal */}
                {showJobDonePanel === req.id && (
  		  <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
  		    <Card className="w-full max-w-md">
		      <CardHeader>
		        <CardTitle className="text-2xl">Job Completed</CardTitle>
		        <CardDescription>Enter the final amount to charge the client</CardDescription>
		      </CardHeader>
		      <CardContent className="space-y-6">
		        <div>
		          <label className="text-sm font-medium text-foreground mb-2 block">
		            Final Amount (KSh)
		          </label>
		          <Input
		            type="number"
		            value={jobDoneAmount}
		            onChange={(e) => setJobDoneAmount(e.target.value)}
		            placeholder={`Minimum: ${getMinCharge(req)}`}
		            className="text-lg"
		          />
		        </div>

		        <div>
		          <label className="text-sm font-medium text-foreground mb-2 block">
		            M-Pesa Number to Receive Payment
		          </label>
		          <Input
		            type="text"
		            value={payoutPhone}
		            onChange={(e) => setPayoutPhone(e.target.value)}
		            placeholder="07xxxxxxxx"
		          />
		        </div>
		      </CardContent>
		
		      <CardFooter className="flex gap-3">
		        <Button
		          onClick={() => markJobCompleteMutation.mutate({
		            requestId: req.id,
		            amount: jobDoneAmount,
		            provider_phone: payoutPhone,
		          })}
		          disabled={!jobDoneAmount || !payoutPhone || markJobCompleteMutation.isPending}
		          className="flex-1"
		        >
		          {markJobCompleteMutation.isPending ? "Sending..." : "Send Payment Request"}
		        </Button>
		        <Button
		          variant="outline"
		          onClick={() => {
		            setShowJobDonePanel(null);
		            setJobDoneAmount('');
		            setPayoutPhone('');
		          }}
		        >
		          Cancel
		        </Button>
		      </CardFooter>
		    </Card>
		  </div>
		)}

                {/* Client Payment Modal */}
                {showPaymentRequestPanel === req.id && (
		  <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
		    <Card className="w-full max-w-md">
		      <CardHeader>
		        <CardTitle>Pay for Service</CardTitle>
		        <CardDescription>
		          KSh {req.order?.amount} to {req.provider_business_name}
		        </CardDescription>
		      </CardHeader>
		      <CardContent className="space-y-6">
		        <div>
		          <label className="text-sm font-medium mb-2 block">M-Pesa Number</label>
		          <Input
		            type="text"
		            value={clientPayPhone}
		            onChange={(e) => setClientPayPhone(e.target.value)}
		            placeholder="07xxxxxxxx"
		            className="text-base"
		          />
		        </div>
		      </CardContent>
		      <CardFooter className="flex gap-3">
		        <Button
		          onClick={() => {
		            initiateStkPushMutation.mutate({
		              orderId: req.order.id,
		              phone_number: clientPayPhone
		            });
		          }}
		          disabled={!clientPayPhone || initiateStkPushMutation.isPending}
		          className="flex-1"
		        >
		          {initiateStkPushMutation.isPending ? "Sending..." : "Pay via M-Pesa"}
		        </Button>
		        <Button variant="outline" onClick={() => setShowPaymentRequestPanel(null)}>
		          Cancel
		        </Button>
		      </CardFooter>
		    </Card>
		  </div>
		)}

                {/* Rating Form */}
                {!isProviderSection && req.order?.status === 'completed' && !req.order.rating && (
		  <div className="mt-6 pt-6 border-t border-border">
		    <div className="text-sm font-medium text-foreground mb-3">How was your experience?</div>
		    <RatingForm
		      orderId={req.order.id}
		      onSuccess={() => {
		        queryClient.invalidateQueries(['clientOrders']);
		        queryClient.invalidateQueries(['providerOrders']);
		        toast.success('Thank you for your feedback!');
		      }}
		    />
		  </div>
		)}
              </CardContent>
            </Card>  
          );
        })}
      </div>
          <OrdersPagination 
            currentPage={currentPage}
            totalItems={filteredOrders.length}
            onPageChange={setCurrentPage}
          />
        </>
      )}
    </div>
  );
});

const ProgressTimer = ({ requestId }) => {
  const [countdown, setCountdown] = useState('');
  const [isWarning, setIsWarning] = useState(false);
  const [isOverdue, setIsOverdue] = useState(false);
  const { data: progressData } = useQuery({
    queryKey: ['orderProgress', requestId],
    queryFn: () => fetchOrderProgress(requestId),
    enabled: !!requestId,
    refetchInterval: 60000,
  });

  useEffect(() => {
    if (!progressData || progressData.status !== 'active') {
      setCountdown('');
      setIsWarning(false);
      setIsOverdue(false);
      return;
    }
    const updateCountdown = () => {
      const now = new Date();
      const isOverdue = progressData.is_overdue;
      const lastUpdated = progressData.last_updated ? new Date(progressData.last_updated) : new Date(progressData.active_at);
      const deadline = new Date(lastUpdated.getTime() + 24 * 60 * 60 * 1000);
      const warningTime = new Date(lastUpdated.getTime() + 20 * 60 * 60 * 1000);
      const timeRemaining = deadline - now;
      if (isOverdue || timeRemaining <= 0) {
        setCountdown('Overdue');
        setIsOverdue(true);
        setIsWarning(true);
      } else {
        const hours = Math.floor(timeRemaining / (1000 * 60 * 60));
        const minutes = Math.floor((timeRemaining % (1000 * 60 * 60)) / (1000 * 60));
        setCountdown(`${hours}h ${minutes}m`);
        setIsOverdue(false);
        setIsWarning(now >= warningTime);
      }
    };
    updateCountdown();
    const interval = setInterval(updateCountdown, 60000);
    return () => clearInterval(interval);
  }, [progressData]);

  if (!countdown) return null;
  return (
    <p>
      Next confirmation due in{' '}
      <span className={isOverdue ? 'text-red-500' : isWarning ? 'text-yellow-500' : 'text-green-500'}>
        {countdown}
      </span>
      {isWarning && !isOverdue && (
        <span className="text-yellow-500 ml-2">Confirm within 4 hours to avoid cancellation!</span>
      )}
      {isOverdue && (
        <span className="text-red-500 ml-2">Order overdue; awaiting cancellation.</span>
      )}
    </p>
  );
};

function ManageOrders() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const orderRefs = useRef({});
  const [error, setError] = useState('');
  const [statusSocket, setStatusSocket] = useState(null);
  const [debtSocket, setDebtSocket] = useState(null);
  const [showJobDonePanel, setShowJobDonePanel] = useState(null);
  const [showPaymentRequestPanel, setShowPaymentRequestPanel] = useState(null);
  const [jobDoneAmount, setJobDoneAmount] = useState('');
  const [payoutPhone, setPayoutPhone] = useState('');
  const [clientPayPhone, setClientPayPhone] = useState('');
  const [paymentStates, setPaymentStates] = useState({});
  const [pendingActiveIncomingPage, setPendingActiveIncomingPage] = useState(1);
  const [completedIncomingPage, setCompletedIncomingPage] = useState(1);
  const [pendingActiveOutgoingPage, setPendingActiveOutgoingPage] = useState(1);
  const [completedOutgoingPage, setCompletedOutgoingPage] = useState(1);
  const [pendingActiveIncomingFilters, setPendingActiveIncomingFilters] = useState({ statuses: [], search: '', searchInput: {} });
  const [completedIncomingFilters, setCompletedIncomingFilters] = useState({ statuses: [], search: '', searchInput: {} });
  const [pendingActiveOutgoingFilters, setPendingActiveOutgoingFilters] = useState({ statuses: [], search: '', searchInput: {} });
  const [completedOutgoingFilters, setCompletedOutgoingFilters] = useState({ statuses: [], search: '', searchInput: {} });
  const [isAuthLoading, setIsAuthLoading] = useState(false);
  const { user, userLoading, userError } = useAuth();
  const { darkMode, toggleDarkMode } = useTheme();

  const { data: providerOrdersRaw, isLoading: providerLoading } = useQuery({
    queryKey: ['providerOrders'],
    queryFn: fetchOrders,
    enabled: !!user?.is_service_provider,
    retry: 2,
    staleTime: 1000 * 60,
    onError: (err) => {
      setError(err.response?.data?.error || 'Failed to load incoming orders.');
      toast.error('Failed to load incoming orders.');
    },
  });

  const { data: clientOrdersRaw, isLoading: clientLoading } = useQuery({
    queryKey: ['clientOrders'],
    queryFn: fetchClientOrders,
    enabled: !!user,
    staleTime: 1000 * 60,
    onError: () => toast.error('Failed to load outgoing orders.'),
  });

  const providerOrders = useMemo(() => {
    if (!providerOrdersRaw) return null;
    const incoming_requests = [
      ...(providerOrdersRaw.pending_active_incoming || []),
      ...(providerOrdersRaw.completed_incoming || []),
      ...(providerOrdersRaw.rejected_voided_incoming || []),
    ];
    return { incoming_requests };
  }, [providerOrdersRaw]);

  const clientOrders = useMemo(() => {
    if (!clientOrdersRaw) return null;
    const service_requests = [
      ...(clientOrdersRaw.pending_active_requests || []),
      ...(clientOrdersRaw.completed_requests || []),
      ...(clientOrdersRaw.rejected_voided_requests || []),
    ];
    return { service_requests };
  }, [clientOrdersRaw]);

  const getMinCharge = useCallback((req) => {
    const services = req?.services || [];
    if (services.length === 0) return 300;
    let highestCharge = 0;
    for (const service of services) {
      if (
        service.is_general_category &&
        service.custom_minimum_charge &&
        service.custom_minimum_charge > 0
      ) {
        highestCharge = Math.max(highestCharge, service.custom_minimum_charge);
      }
      else if (service.category_default_min_charge) {
        highestCharge = Math.max(highestCharge, service.category_default_min_charge);
      }
    }
    return highestCharge > 0 ? highestCharge : 300;
  }, []);

  const acceptMutation = useMutation({
    mutationFn: (requestId) =>
      axios.post(`${API_URL}/api/request/${requestId}/accept/`, {}, { withCredentials: true }),
    onSuccess: () => {
      queryClient.invalidateQueries(['providerOrders', 'clientOrders']);
      queryClient.refetchQueries(['providerOrders', 'clientOrders']);
      toast.success('Request accepted!');
    },
    onError: (err) => toast.error(err.response?.data?.error || 'Failed to accept'),
  });

  const rejectMutation = useMutation({
    mutationFn: (requestId) =>
      axios.post(`${API_URL}/api/request/${requestId}/reject/`, {}, { withCredentials: true }),
    onSuccess: () => {
      queryClient.invalidateQueries(['providerOrders', 'clientOrders']);
      queryClient.refetchQueries(['providerOrders', 'clientOrders']);
      toast.success('Request rejected');
    },
    onError: (err) => toast.error(err.response?.data?.error || 'Failed to reject'),
  });

  const markJobCompleteMutation = useMutation({
    mutationFn: ({ requestId, amount, provider_phone }) =>
      axios.post(
        `${API_URL}/api/request/${requestId}/complete/`,
        { amount, provider_phone },
        { withCredentials: true }
      ),
    onSuccess: () => {
      toast.success('Payment request sent!');
      setShowJobDonePanel(null);
      setJobDoneAmount('');
      setPayoutPhone('');
      queryClient.invalidateQueries(['providerOrders', 'clientOrders']);
      queryClient.refetchQueries(['providerOrders', 'clientOrders']);
    },
    onError: (err) => toast.error(err.response?.data?.error || 'Failed to send request'),
  });

  const initiateStkPushMutation = useMutation({
    mutationFn: ({ orderId, phone_number }) =>
      axios.post(
        `${API_URL}/api/order/${orderId}/stk_push/`,
        { phone_number },
        { withCredentials: true }
      ),
    onSuccess: (data, variables) => {
      const orderId = variables.orderId;
      toast.success('STK Push sent! Complete payment on your phone.');
      setShowPaymentRequestPanel(null);
      setClientPayPhone('');
      setPaymentStates(prev => ({ ...prev, [orderId]: 'processing' }));
      queryClient.setQueryData(['clientOrders'], (old) => {
        if (!old?.service_requests) return old;
        return {
          ...old,
          service_requests: old.service_requests.map(req =>
            req.order?.id === orderId
              ? { ...req, order: { ...req.order, status: 'processing' } }
              : req
          ),
        };
      });
    },
    onError: (err) => toast.error(err.response?.data?.error || 'STK Push failed'),
  });

  const handleAccept = useCallback((requestId) => acceptMutation.mutate(requestId), [acceptMutation]);
  const handleReject = useCallback((requestId) => rejectMutation.mutate(requestId), [rejectMutation]);

  const handleLogout = async () => {
    try {
      setIsAuthLoading(true);
      await axios.post(`${API_URL}/api/logout/`, {}, { withCredentials: true });
      queryClient.clear();
      navigate('/');
      toast.success('Logged out successfully.');
    } catch {
      toast.error('Failed to logout.');
    } finally {
      setIsAuthLoading(false);
    }
  };

  const getStatusColor = useMemo(() => (status) => {
    const map = {
      pending: 'text-gray-500',
      scheduled: 'text-yellow-500',
      active: 'text-green-500',
      ready_for_payment: 'text-blue-500',
      pending_payment: 'text-blue-500',
      processing: 'text-blue-500',
      completed: 'text-purple-500',
      rejected: 'text-red-500',
      voided: 'text-red-500',
      cancelled: 'text-red-500',
    };
    return map[status] || 'text-white';
  }, []);

  const getStatusBadgeVariant = (status) => {
    switch (status?.toLowerCase()) {
      case 'pending':      return 'bg-yellow-500/90 hover:bg-yellow-500 text-white';
      case 'scheduled':    return 'bg-amber-500/90 hover:bg-amber-500 text-white';
      case 'active':       return 'bg-emerald-500 hover:bg-emerald-600 text-white';
      case 'ready_for_payment':
      case 'pending_payment': return 'bg-blue-500 hover:bg-blue-600 text-white';
      case 'processing':   return 'bg-purple-500 hover:bg-purple-600 text-white';
      case 'completed':    return 'bg-violet-600 hover:bg-violet-700 text-white';
      case 'rejected':
      case 'voided':
      case 'cancelled':    return 'bg-red-500 hover:bg-red-600 text-white';
      default:             return 'bg-gray-500 hover:bg-gray-600 text-white';
    }
  };

  useEffect(() => {
    if (!user) return;

    let unsubscribeFcm = null;
    if (messaging) {
      unsubscribeFcm = onMessage(messaging, (payload) => {
        const msg = payload.notification?.body || payload.data?.message || 'New update from SErraND';
        toast.success(msg, { duration: 8000, position: 'top-center' });
        queryClient.invalidateQueries(['providerOrders']);
        queryClient.invalidateQueries(['clientOrders']);
      });
    }

    const statusWs = new WebSocket(`${WS_URL}/ws/status/`);

    statusWs.onmessage = (event) => {
      const data = JSON.parse(event.data);

      if (data.type === 'status_update') {
        // Force refresh both lists
        queryClient.refetchQueries(['clientOrders']);
        queryClient.refetchQueries(['providerOrders']);

        // PAYMENT SUCCESS → instant UI update
        if (data.order_status === 'completed' && data.order_id) {
          const orderId = data.order_id;
          if (paymentStates[orderId] === 'processing') {
            setPaymentStates(prev => ({ ...prev, [orderId]: 'success' }));
            toast.success('Payment completed successfully!');
          }
        }
      }
    };

    statusWs.onopen = () => console.log('Status WebSocket connected');
    statusWs.onclose = () => console.log('Status WebSocket closed');
    setStatusSocket(statusWs);

    // Auto-reset stuck processing orders (safety net)
    const stuckOrderTimer = setInterval(() => {
      queryClient.setQueryData(['clientOrders'], (oldData) => {
        if (!oldData?.service_requests) return oldData;
        const now = Date.now();
        const updatedRequests = oldData.service_requests.map((req) => {
          if (
            req.order?.status === 'processing' &&
            req.order?.updated_at
          ) {
            const updatedTime = new Date(req.order.updated_at).getTime();
            if (now - updatedTime > 15000) {
              return {
                ...req,
                order: { ...req.order, status: 'pending_payment' },
              };
            }
          }
          return req;
        });
        return { ...oldData, service_requests: updatedRequests };
      });
    }, 10000);

    // 15-second buffer for failed payments
    const timeout = setTimeout(() => {
      Object.keys(paymentStates).forEach(id => {
        if (paymentStates[id] === 'processing') {
          setPaymentStates(prev => ({ ...prev, [id]: 'failed' }));
          toast.error('Payment not detected. Please retry if you paid.');
        }
      });
    }, 15000);

    return () => {
      if (unsubscribeFcm) unsubscribeFcm();
      if (statusWs.readyState === WebSocket.OPEN || statusWs.readyState === WebSocket.CONNECTING) {
        statusWs.close();
      }
      clearInterval(stuckOrderTimer);
      clearTimeout(timeout);
    };
  }, [user, queryClient, messaging, paymentStates]);

  if (userLoading) return <div className="text-center text-white py-12">Loading user data...</div>;
  if (!user || userError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <h2 className="text-3xl font-bold mb-6">Login Required</h2>
          <div className="flex justify-center gap-4">
            <Button onClick={() => navigate('/', { state: { showLogin: true } })}>
              Log In
            </Button>
            <Button onClick={() => navigate('/', { state: { showRegister: true } })}>
              Sign Up
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background relative overflow-hidden">
      <Meteors count={40} angle={220} />
      <Navbar
        user={user}
        userLoading={userLoading}
        isAuthLoading={isAuthLoading}
        handleLogout={handleLogout}
        toggleDarkMode={toggleDarkMode}
        darkMode={darkMode}
      />

      <div className="orders-content p-6 max-w-7xl mx-auto relative z-10">
        {error && <div className="text-red-500 text-center mb-6 font-medium">{error}</div>}

        {user?.is_service_provider && providerOrders && (
          <>
            <OrderSection
              title="Active & Pending Incoming Orders"
              orders={providerOrders.incoming_requests.filter(r => !['completed','rejected','voided','cancelled'].includes(r.status))}
              filters={pendingActiveIncomingFilters}
              setFilters={setPendingActiveIncomingFilters}
              currentPage={pendingActiveIncomingPage}
              setCurrentPage={setPendingActiveIncomingPage}
              isCompleted={false}
              isProviderSection={true}
              sectionKey="pendingActiveIncoming"
              providerOrders={providerOrders}
              handleAccept={handleAccept}
              handleReject={handleReject}
              getStatusBadgeVariant={getStatusBadgeVariant}
              getStatusColor={getStatusColor}
              showJobDonePanel={showJobDonePanel}
              setShowJobDonePanel={setShowJobDonePanel}
              showPaymentRequestPanel={showPaymentRequestPanel}
              setShowPaymentRequestPanel={setShowPaymentRequestPanel}
              jobDoneAmount={jobDoneAmount}
              setJobDoneAmount={setJobDoneAmount}
              payoutPhone={payoutPhone}
              setPayoutPhone={setPayoutPhone}
              clientPayPhone={clientPayPhone}
              setClientPayPhone={setClientPayPhone}
              queryClient={queryClient}
              user={user}
              orderRefs={orderRefs}
              getMinCharge={getMinCharge}
              acceptMutation={acceptMutation}
              rejectMutation={rejectMutation}
              markJobCompleteMutation={markJobCompleteMutation}
              initiateStkPushMutation={initiateStkPushMutation}
              paymentStates={paymentStates}
              setPaymentStates={setPaymentStates}
            />
            <OrderSection
              title="Completed & Rejected Incoming Orders"
              orders={providerOrders.incoming_requests.filter(r => ['completed','rejected','voided','cancelled'].includes(r.status))}
              filters={completedIncomingFilters}
              setFilters={setCompletedIncomingFilters}
              currentPage={completedIncomingPage}
              setCurrentPage={setCompletedIncomingPage}
              isCompleted={true}
              isProviderSection={true}
              sectionKey="completedIncoming"
              providerOrders={providerOrders}
              handleAccept={handleAccept}
              handleReject={handleReject}
              getStatusColor={getStatusColor}
              getStatusBadgeVariant={getStatusBadgeVariant}
              showJobDonePanel={showJobDonePanel}
              setShowJobDonePanel={setShowJobDonePanel}
              showPaymentRequestPanel={showPaymentRequestPanel}
              setShowPaymentRequestPanel={setShowPaymentRequestPanel}
              jobDoneAmount={jobDoneAmount}
              setJobDoneAmount={setJobDoneAmount}
              payoutPhone={payoutPhone}
              setPayoutPhone={setPayoutPhone}
              clientPayPhone={clientPayPhone}
              setClientPayPhone={setClientPayPhone}
              queryClient={queryClient}
              user={user}
              orderRefs={orderRefs}
              getMinCharge={getMinCharge}
              acceptMutation={acceptMutation}
              rejectMutation={rejectMutation}
              markJobCompleteMutation={markJobCompleteMutation}
              initiateStkPushMutation={initiateStkPushMutation}
              paymentStates={paymentStates}
              setPaymentStates={setPaymentStates}
            />
          </>
        )}

        {clientOrders && (
          <>
            <OrderSection
              title="Active & Pending Outgoing Orders"
              orders={clientOrders.service_requests.filter(r => !['completed','rejected','voided','cancelled'].includes(r.status))}
              filters={pendingActiveOutgoingFilters}
              setFilters={setPendingActiveOutgoingFilters}
              currentPage={pendingActiveOutgoingPage}
              setCurrentPage={setPendingActiveOutgoingPage}
              isCompleted={false}
              isProviderSection={false}
              sectionKey="pendingActiveOutgoing"
              providerOrders={providerOrders}
              handleAccept={handleAccept}
              handleReject={handleReject}
              getStatusColor={getStatusColor}
              getStatusBadgeVariant={getStatusBadgeVariant}
              showJobDonePanel={showJobDonePanel}
              setShowJobDonePanel={setShowJobDonePanel}
              showPaymentRequestPanel={showPaymentRequestPanel}
              setShowPaymentRequestPanel={setShowPaymentRequestPanel}
              jobDoneAmount={jobDoneAmount}
              setJobDoneAmount={setJobDoneAmount}
              payoutPhone={payoutPhone}
              setPayoutPhone={setPayoutPhone}
              clientPayPhone={clientPayPhone}
              setClientPayPhone={setClientPayPhone}
              queryClient={queryClient}
              user={user}
              orderRefs={orderRefs}
              getMinCharge={getMinCharge}
              acceptMutation={acceptMutation}
              rejectMutation={rejectMutation}
              markJobCompleteMutation={markJobCompleteMutation}
              initiateStkPushMutation={initiateStkPushMutation}
              paymentStates={paymentStates}
              setPaymentStates={setPaymentStates}
            />
            <OrderSection
              title="Completed & Rejected Outgoing Orders"
              orders={clientOrders.service_requests.filter(r => ['completed','rejected','voided','cancelled'].includes(r.status))}
              filters={completedOutgoingFilters}
              setFilters={setCompletedOutgoingFilters}
              currentPage={completedOutgoingPage}
              setCurrentPage={setCompletedOutgoingPage}
              isCompleted={true}
              isProviderSection={false}
              sectionKey="completedOutgoing"
              providerOrders={providerOrders}
              handleAccept={handleAccept}
              handleReject={handleReject}
              getStatusColor={getStatusColor}
              getStatusBadgeVariant={getStatusBadgeVariant}
              showJobDonePanel={showJobDonePanel}
              setShowJobDonePanel={setShowJobDonePanel}
              showPaymentRequestPanel={showPaymentRequestPanel}
              setShowPaymentRequestPanel={setShowPaymentRequestPanel}
              jobDoneAmount={jobDoneAmount}
              setJobDoneAmount={setJobDoneAmount}
              payoutPhone={payoutPhone}
              setPayoutPhone={setPayoutPhone}
              clientPayPhone={clientPayPhone}
              setClientPayPhone={setClientPayPhone}
              queryClient={queryClient}
              user={user}
              orderRefs={orderRefs}
              getMinCharge={getMinCharge}
              acceptMutation={acceptMutation}
              rejectMutation={rejectMutation}
              markJobCompleteMutation={markJobCompleteMutation}
              initiateStkPushMutation={initiateStkPushMutation}
              paymentStates={paymentStates}
              setPaymentStates={setPaymentStates}
            />
          </>
        )}

        {(providerLoading || clientLoading) && (
          <div className="text-center text-white py-12">Loading orders...</div>
        )}
      </div>
    </div>
  );
}

export default ManageOrders;
